import React, { useState, useRef, useEffect } from 'react';
import type { SchoolClassDto, UserDto } from '../../api/axios';
import styles from './TimetableGrid.module.css';

interface TimetableToolbarProps {
  viewMode: 'student' | 'teacher';
  onViewModeChange: (mode: 'student' | 'teacher') => void;
  classes: SchoolClassDto[];
  activeForm: number;
  onFormChange: (form: number) => void;
  teacherAccounts: UserDto[];
  selectedTeacherId: number | null;
  onTeacherChange: (id: number | null) => void;
  onPublish: () => void;
  onGenerate: () => void;
  onClear: () => void;
  publishing: boolean;
  generating: boolean;
  clearing: boolean;
}

const TimetableToolbar: React.FC<TimetableToolbarProps> = ({
  viewMode,
  onViewModeChange,
  classes,
  activeForm,
  onFormChange,
  teacherAccounts,
  selectedTeacherId,
  onTeacherChange,
  onPublish,
  onGenerate,
  onClear,
  publishing,
  generating,
  clearing,
}) => {
  const [showMobileActions, setShowMobileActions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowMobileActions(false);
      }
    };
    if (showMobileActions) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showMobileActions]);

  const activeClasses = classes.filter(c => c.status === 'active');

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarTop}>
        <div className={styles.viewToggle}>
          <button
            className={`${styles.viewToggleBtn} ${viewMode === 'student' ? styles.active : ''}`}
            onClick={() => onViewModeChange('student')}
          >
            Student
          </button>
          <button
            className={`${styles.viewToggleBtn} ${viewMode === 'teacher' ? styles.active : ''}`}
            onClick={() => onViewModeChange('teacher')}
          >
            Teacher
          </button>
        </div>

        {viewMode === 'student' && (
          <>
            <div className={styles.actionBtns}>
              <button
                className={`${styles.actionBtn} ${styles.publish}`}
                onClick={onPublish}
                disabled={publishing}
              >
                {publishing ? 'Publishing...' : 'Publish'}
              </button>
              <button
                className={`${styles.actionBtn} ${styles.generate}`}
                onClick={onGenerate}
                disabled={generating}
              >
                {generating ? 'Generating...' : 'Generate'}
              </button>
              <button
                className={`${styles.actionBtn} ${styles.clear}`}
                onClick={onClear}
                disabled={clearing}
              >
                {clearing ? 'Clearing...' : 'Clear All'}
              </button>
            </div>

            <div className={styles.mobileActionsBtn} ref={dropdownRef} onClick={() => setShowMobileActions(p => !p)}>
              Actions ▾
              {showMobileActions && (
                <div className={styles.mobileActionsDropdown} onClick={e => e.stopPropagation()}>
                  <button className={`${styles.actionBtn} ${styles.publish}`} onClick={() => { onPublish(); setShowMobileActions(false); }} disabled={publishing}>
                    {publishing ? 'Publishing...' : 'Publish'}
                  </button>
                  <button className={`${styles.actionBtn} ${styles.generate}`} onClick={() => { onGenerate(); setShowMobileActions(false); }} disabled={generating}>
                    {generating ? 'Generating...' : 'Generate'}
                  </button>
                  <button className={`${styles.actionBtn} ${styles.clear}`} onClick={() => { onClear(); setShowMobileActions(false); }} disabled={clearing}>
                    {clearing ? 'Clearing...' : 'Clear All'}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {viewMode === 'student' ? (
        <div className={styles.formTabs}>
          {activeClasses.map(classItem => (
            <button
              key={classItem.id}
              className={`${styles.formTab} ${activeForm === classItem.level ? styles.active : ''}`}
              onClick={() => onFormChange(classItem.level)}
            >
              {classItem.name}
            </button>
          ))}
        </div>
      ) : (
        <div className={styles.teacherSelect}>
          <label>Select Teacher</label>
          <select
            value={selectedTeacherId ?? ''}
            onChange={e => onTeacherChange(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">-- Select a Teacher --</option>
            {teacherAccounts.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} {t.teacher_id ? `(${t.teacher_id})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default TimetableToolbar;
